import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Activity, 
  Bell, 
  CheckCircle, 
  AlertTriangle,
  RefreshCw,
  Clock,
  Zap,
  Filter,
  Eye,
  Check,
  ChevronLeft,
  ChevronRight,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Events = () => {
  const [activeTab, setActiveTab] = useState("events");
  
  // Data
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  
  // Filters
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [branchOptions, setBranchOptions] = useState([]);                // ["Unit 1 Vedica", ...]
  const [startDate, setStartDate] = useState("");                       // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");                           // YYYY-MM-DD
  const [alertFilter, setAlertFilter] = useState("unread");

  // Pagination (server-side) for the Event Log table.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);              // 1-indexed
  const [totalEvents, setTotalEvents] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalEvents / PAGE_SIZE));

  // Inventory-event View modal
  const [viewEvent, setViewEvent] = useState(null);

  const [loading, setLoading] = useState(true);

  // Fetch the static / aggregate data once on mount.
  useEffect(() => {
    fetchAuxData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to page 1 whenever any filter changes.
  useEffect(() => {
    setPage(1);
  }, [eventTypeFilter, moduleFilter, branchFilter, startDate, endDate]);

  // Refetch event page whenever the page or filter changes.
  useEffect(() => {
    fetchEventsPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, eventTypeFilter, moduleFilter, branchFilter, startDate, endDate]);

  const fetchAuxData = async () => {
    try {
      const [alertsRes, statsRes, subsRes, typesRes, branchesRes] = await Promise.all([
        axios.get(`${API}/events/alerts?limit=50`),
        axios.get(`${API}/events/stats`),
        axios.get(`${API}/events/subscriptions`),
        axios.get(`${API}/events/types`),
        axios.get(`${API}/branches/names`).catch(() => ({ data: { branches: [] } })),
      ]);
      setAlerts(alertsRes.data);
      setStats(statsRes.data);
      setSubscriptions(subsRes.data);
      setEventTypes(typesRes.data.event_types || []);
      setBranchOptions(branchesRes.data?.branches || []);
    } catch (error) {
      toast.error("Failed to fetch event data");
    }
  };

  const fetchEventsPage = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams();
      params.set("limit", PAGE_SIZE);
      params.set("skip", skip);
      if (eventTypeFilter && eventTypeFilter !== "all") params.set("event_type", eventTypeFilter);
      if (moduleFilter && moduleFilter !== "all") params.set("source_module", moduleFilter);
      if (branchFilter && branchFilter !== "all") params.set("branch", branchFilter);
      // start_date is inclusive; end_date is exclusive — bump by 1 day so
      // a "to" picker of 2026-06-04 includes everything on the 4th.
      if (startDate) params.set("start_date", `${startDate}T00:00:00`);
      if (endDate) {
        const d = new Date(`${endDate}T00:00:00`);
        d.setDate(d.getDate() + 1);
        params.set("end_date", d.toISOString().slice(0, 19));
      }
      const res = await axios.get(`${API}/events/recent?${params.toString()}`);
      setEvents(res.data.events || []);
      setTotalEvents(res.data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch events");
    }
    setLoading(false);
  }, [page, eventTypeFilter, moduleFilter, branchFilter, startDate, endDate]);

  const fetchAllData = () => {
    fetchAuxData();
    fetchEventsPage();
  };

  const handleMarkAlertRead = async (alertId) => {
    try {
      await axios.put(`${API}/events/alerts/${alertId}/read`);
      toast.success("Alert marked as read");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to mark alert as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/events/alerts/read-all`);
      toast.success("All alerts marked as read");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to mark alerts as read");
    }
  };

  // ─── Inventory event export (Feb 2026) ──────────────────────────────
  // The Export button is enabled ONLY when ALL gates are satisfied:
  //   • event_type ∈ EXPORTABLE_EVENT_TYPES
  //   • branch ≠ "all"
  //   • startDate set
  //   • endDate set
  // This stops anyone from accidentally streaming the entire event log.
  const EXPORTABLE_EVENT_TYPES = ["RM_STOCK_UPDATED", "FG_INVENTORY_UPDATED"];
  const isExportable =
    EXPORTABLE_EVENT_TYPES.includes(eventTypeFilter) &&
    branchFilter !== "all" &&
    !!startDate &&
    !!endDate;

  const handleExport = async () => {
    if (!isExportable) return;
    try {
      // Endpoint contract: start_date inclusive, end_date exclusive — bump by 1 day
      const endD = new Date(`${endDate}T00:00:00`);
      endD.setDate(endD.getDate() + 1);
      const params = new URLSearchParams({
        event_type: eventTypeFilter,
        branch: branchFilter,
        start_date: `${startDate}T00:00:00`,
        end_date: endD.toISOString().slice(0, 19),
      });
      if (moduleFilter && moduleFilter !== "all") params.set("source_module", moduleFilter);

      // Use axios so the global request-interceptor injects the Bearer
      // token from `useAuthStore` (the rest of the page does this). A raw
      // fetch reading `localStorage.getItem("token")` returns null and
      // the server rejects with 401 — which surfaces as a useless
      // "Export failed" toast.
      const res = await axios.get(
        `${API}/events/export?${params.toString()}`,
        { responseType: "blob" },
      );

      const blob = res.data;
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `events_${eventTypeFilter}_${startDate}_${endDate}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a);
      a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      // axios error path — try to extract the actual server message. If
      // the server returned a JSON detail (e.g. EVENT_TYPE_NOT_EXPORTABLE,
      // 401 missing creds), we want it in the toast verbatim. The blob
      // responseType means error bodies arrive as Blob; we have to read
      // them out.
      let serverMsg = "";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          // Try JSON first; fall back to plain text.
          try {
            const parsed = JSON.parse(text);
            serverMsg =
              parsed?.detail?.message
              || parsed?.detail
              || parsed?.message
              || text;
          } catch { serverMsg = text; }
        } catch { /* swallow read failures */ }
      } else if (typeof data === "string") {
        serverMsg = data;
      } else if (data && typeof data === "object") {
        serverMsg = data?.detail?.message || data?.detail || data?.message || "";
      }
      const status = err?.response?.status;
      const composed = serverMsg
        ? `Export failed (${status || "no response"}): ${serverMsg}`
        : err?.message
          ? `Export failed: ${err.message}`
          : "Export failed";
      console.error("event export failed", { status, data, err });
      toast.error(composed);
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      HIGH: "bg-red-100 text-red-800",
      MEDIUM: "bg-yellow-100 text-yellow-800",
      LOW: "bg-blue-100 text-blue-800"
    };
    return <Badge className={colors[severity] || "bg-zinc-200"}>{severity}</Badge>;
  };

  const getEventTypeBadge = (eventType) => {
    const typeMap = {
      // Production
      BATCH_COMPLETED: "bg-green-100 text-green-800",
      PRODUCTION_ENTRY_CREATED: "bg-green-100 text-green-800",
      // Quality
      QC_PASSED: "bg-emerald-100 text-emerald-800",
      QC_FAILED: "bg-red-100 text-red-800",
      // Inventory
      RM_STOCK_LOW: "bg-orange-100 text-orange-800",
      RM_STOCK_UPDATED: "bg-blue-100 text-blue-800",
      // SKU
      SKU_CREATED: "bg-purple-100 text-purple-800",
      BOM_FINALIZED: "bg-purple-100 text-purple-800",
      // Logistics
      DISPATCH_SHIPPED: "bg-cyan-100 text-cyan-800",
      IBT_COMPLETED: "bg-cyan-100 text-cyan-800",
      // Invoice
      INVOICE_PAID: "bg-green-100 text-green-800"
    };
    return <Badge className={typeMap[eventType] || "bg-zinc-100 text-zinc-800"}>{eventType}</Badge>;
  };

  // Server-side filtering — keep the variable name for downstream JSX.
  const filteredEvents = events;

  const filteredAlerts = alerts.filter(a => {
    if (alertFilter === "all") return true;
    if (alertFilter === "unread") return !a.is_read;
    return a.is_read;
  });

  const unreadCount = alerts.filter(a => !a.is_read).length;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center" data-testid="events-loading">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Loading event data...
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="events-page">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase">Event System</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">Message Queue & Notifications</p>
        </div>
        <Button variant="outline" onClick={fetchAllData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase text-muted-foreground flex items-center gap-2">
              <Activity className="w-4 h-4" /> Total Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{stats?.total_events || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" /> Last 24h
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-blue-600">{stats?.events_last_24h || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase text-muted-foreground flex items-center gap-2">
              <Bell className="w-4 h-4" /> Unread Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-orange-600">{unreadCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase text-muted-foreground flex items-center gap-2">
              <Zap className="w-4 h-4" /> Handlers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{Object.keys(subscriptions).length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="events" className="uppercase text-xs tracking-wide">
            <Activity className="w-4 h-4 mr-2" />
            Event Log ({totalEvents.toLocaleString()})
          </TabsTrigger>
          <TabsTrigger value="alerts" className="uppercase text-xs tracking-wide">
            <Bell className="w-4 h-4 mr-2" />
            Alerts ({unreadCount} unread)
          </TabsTrigger>
          <TabsTrigger value="handlers" className="uppercase text-xs tracking-wide">
            <Zap className="w-4 h-4 mr-2" />
            Handlers
          </TabsTrigger>
        </TabsList>

        {/* Event Log Tab */}
        <TabsContent value="events">
          <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="events-filter-bar">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <div className="flex flex-col">
                <label className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Event Type</label>
                <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="event-type-filter">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {eventTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Module</label>
              <Select value={moduleFilter} onValueChange={setModuleFilter}>
                <SelectTrigger className="w-[180px]" data-testid="module-filter">
                  <SelectValue placeholder="All modules" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modules</SelectItem>
                  {(stats?.by_module || []).filter(m => m.module).map(m => (
                    <SelectItem key={m.module} value={m.module}>
                      {m.module} ({m.count?.toLocaleString?.() || m.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col">
              <label className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Branch</label>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="w-[200px]" data-testid="branch-filter">
                  <SelectValue placeholder="All branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branchOptions.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col">
              <label className="text-[10px] font-mono uppercase text-muted-foreground mb-1">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9 px-3 rounded-md border border-input bg-white text-sm font-mono"
                data-testid="event-start-date"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[10px] font-mono uppercase text-muted-foreground mb-1">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9 px-3 rounded-md border border-input bg-white text-sm font-mono"
                data-testid="event-end-date"
              />
            </div>

            {(eventTypeFilter !== "all" || moduleFilter !== "all" || branchFilter !== "all" || startDate || endDate) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEventTypeFilter("all");
                  setModuleFilter("all");
                  setBranchFilter("all");
                  setStartDate("");
                  setEndDate("");
                }}
                data-testid="events-clear-filters"
              >
                Clear filters
              </Button>
            )}

            {/* Feb 2026 — Inventory event export. Gated on ALL filters
                being set (event_type ∈ allow-list, branch ≠ all, both
                dates) so users can't accidentally bomb the whole log. */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={!isExportable}
              data-testid="events-export-btn"
              title={
                isExportable
                  ? "Download filtered events as CSV (one row per item)"
                  : "Select event type (RM/FG stock update), branch, and date range to enable export"
              }
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
          </div>

          <div className="border rounded-sm">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Timestamp</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Event Type</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Module</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Payload</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Handlers</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase w-20">Detail</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <tr key={event.id} className="border-t">
                    <td className="p-4 text-sm font-mono">
                      {new Date(event.timestamp).toLocaleString()}
                    </td>
                    <td className="p-4">{getEventTypeBadge(event.event_type)}</td>
                    <td className="p-4 text-sm">{event.source_module}</td>
                    <td className="p-4 text-xs font-mono max-w-[300px] truncate">
                      {(() => {
                        const p = event.payload || {};
                        // Compact summary for inventory events
                        if (p.item_kind && Array.isArray(p.items)) {
                          return `${p.business_event_type || ""} · ${p.branch || ""} · ${p.items.length} item(s)`;
                        }
                        return JSON.stringify(p);
                      })()}
                    </td>
                    <td className="p-4 text-xs">
                      {(event.handlers_triggered || []).map(h => (
                        <Badge key={h} variant="outline" className="mr-1 text-xs">{h}</Badge>
                      ))}
                    </td>
                    <td className="p-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setViewEvent(event)}
                        data-testid={`event-view-btn-${event.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredEvents.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No events found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer — Prev/Next sized big enough to clear the
              fixed "Made with Emergent" badge at the bottom-right. The
              `mb-24` reserves enough room beneath the buttons so the badge
              (≈80×40 px with margin) never overlaps them. */}
          {totalEvents > 0 && (
            <div
              className="mt-4 mb-24 flex flex-wrap items-center justify-end gap-4"
              data-testid="events-pagination"
            >
              <div className="text-sm font-mono text-muted-foreground" data-testid="events-pagination-range">
                {(() => {
                  const start = (page - 1) * PAGE_SIZE + 1;
                  const end = Math.min(page * PAGE_SIZE, totalEvents);
                  return `${start.toLocaleString()}–${end.toLocaleString()} of ${totalEvents.toLocaleString()}`;
                })()}
              </div>
              <div className="text-sm font-mono text-muted-foreground" data-testid="events-pagination-pageinfo">
                Page {page} of {totalPages}
              </div>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                data-testid="events-pagination-prev"
                className="h-10 px-5 text-sm"
              >
                <ChevronLeft className="w-5 h-5 mr-1.5" /> Prev
              </Button>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                data-testid="events-pagination-next"
                className="h-10 px-5 text-sm"
              >
                Next <ChevronRight className="w-5 h-5 ml-1.5" />
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={alertFilter} onValueChange={setAlertFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Alerts</SelectItem>
                  <SelectItem value="unread">Unread Only</SelectItem>
                  <SelectItem value="read">Read Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {unreadCount > 0 && (
              <Button variant="outline" onClick={handleMarkAllRead}>
                <Check className="w-4 h-4 mr-2" /> Mark All Read
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {filteredAlerts.map((alert) => (
              <Card key={alert.id} className={alert.is_read ? "opacity-60" : ""}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {alert.alert_type === "LOW_STOCK" && <AlertTriangle className="w-4 h-4 text-orange-600" />}
                        {alert.alert_type === "QC_FAILURE" && <AlertTriangle className="w-4 h-4 text-red-600" />}
                        {getSeverityBadge(alert.severity)}
                        <Badge variant="outline">{alert.alert_type}</Badge>
                        {alert.is_read && <Badge className="bg-zinc-100">Read</Badge>}
                      </div>
                      <p className="text-sm">{alert.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {alert.entity_type}: {alert.entity_id} | {alert.branch || 'N/A'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(alert.created_at).toLocaleString()}
                      </p>
                    </div>
                    {!alert.is_read && (
                      <Button variant="ghost" size="sm" onClick={() => handleMarkAlertRead(alert.id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredAlerts.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                No alerts to show
              </div>
            )}
          </div>
        </TabsContent>

        {/* Handlers Tab */}
        <TabsContent value="handlers">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(subscriptions).map(([eventType, handlers]) => (
              <Card key={eventType}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{getEventTypeBadge(eventType)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {handlers.map(h => (
                      <div key={h} className="flex items-center gap-2 text-sm">
                        <Zap className="w-3 h-3 text-yellow-500" />
                        <span className="font-mono text-xs">{h}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-sm font-mono">All Event Types ({eventTypes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {eventTypes.map(t => (
                  <Badge key={t.value} variant="outline" className="text-xs">
                    {t.name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Event Detail Modal (Feb 2026 — View payload, esp. inventory events) */}
      {viewEvent && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setViewEvent(null)}
          data-testid="event-detail-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <div className="text-xs uppercase tracking-wide text-zinc-500 font-mono">
                  {viewEvent.event_type}
                </div>
                <div className="text-sm font-medium mt-0.5">
                  {viewEvent.source_module} · {new Date(viewEvent.timestamp).toLocaleString()}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setViewEvent(null)}>✕</Button>
            </div>

            {/* Single scrollable body — the inventory table can have many
                rows and the JSON payload is always long; sharing one
                `flex-1 overflow-y-auto` container ensures both surface
                instead of the table clipping the payload below. */}
            <div className="flex-1 overflow-y-auto" data-testid="event-detail-body">
              {/* Inventory-event item table (only renders when payload matches) */}
              {viewEvent.payload?.item_kind && Array.isArray(viewEvent.payload?.items) && (
                <div className="px-4 pt-4 space-y-2">
                  <div className="text-xs text-zinc-600">
                    <span className="font-semibold">{viewEvent.payload.business_event_type}</span>
                    {viewEvent.payload.branch && <span> · {viewEvent.payload.branch}</span>}
                    {viewEvent.payload.entity_id && (
                      <span> · <code className="text-[11px]">{viewEvent.payload.entity_id}</code></span>
                    )}
                    <span className="ml-2 text-zinc-400">({viewEvent.payload.items.length} item{viewEvent.payload.items.length === 1 ? "" : "s"})</span>
                  </div>
                  <div className="border rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-mono uppercase">Item ID</th>
                          <th className="px-3 py-1.5 text-right font-mono uppercase">Starting</th>
                          <th className="px-3 py-1.5 text-right font-mono uppercase">Δ</th>
                          <th className="px-3 py-1.5 text-right font-mono uppercase">Ending</th>
                          <th className="px-3 py-1.5 text-left font-mono uppercase">UOM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewEvent.payload.items.map((it, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-1.5 font-mono">{it.item_id}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{it.starting ?? "—"}</td>
                            <td className={`px-3 py-1.5 text-right font-mono ${Number(it.change) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                              {Number(it.change) >= 0 ? "+" : ""}{it.change}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono">{it.ending ?? "—"}</td>
                            <td className="px-3 py-1.5">{it.uom || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="p-4">
                <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Full Payload</div>
                <pre className="bg-zinc-900 text-zinc-100 p-3 rounded text-[11px] overflow-x-auto font-mono">
                  {JSON.stringify(viewEvent, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
